import { forwardRef } from 'react';

import Collapse from '@liquidweb-dev/undercurrent/Collapse';
import InfoIcon from '@liquidweb-dev/undercurrent/icons/lucide/Info';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Note from '@liquidweb-dev/undercurrent/Note';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import combineSx from '@liquidweb-dev/undercurrent/utility/combineSx';

import HookedCheckbox from 'components/standard/Form/HookedCheckbox';
import HookedCountrySelect from 'components/standard/Form/HookedCountrySelect';
import HookedRegionSelect from 'components/standard/Form/HookedRegionSelect';
import HookedTextField from 'components/standard/Form/HookedTextField';
import { useSslDvFlatDetails } from 'modules/queries/product/useFlatDetails';
import useServerWhmDnsListzones from 'modules/queries/server/whm/dns/useListzones';
import useCertificateVerification from 'modules/queries/ssl/certificate/useVerification';
import Autorenew from '../Autorenew';
import TypeCard from '../TypeCard';
import VerificationMethod from '../VerificationMethod';
import ServerCredentialsNote, {
	useSelectedServer,
} from './ServerCredentialsNote';

export interface AutomatedMethodProps extends Omit<StackProps, 'children'> {
	autorenew: boolean;
	country: string;
	domain: string;
	server: string;
	wildcard: boolean;
}

const AutomatedMethod = forwardRef<HTMLDivElement, AutomatedMethodProps>(
	({ autorenew, country, domain, server, wildcard, sx, ...rest }, ref) => {
		const {
			hostServers,
			hasCredentials,
			hasSniEnabled,
			isLoading: isHostServersLoading,
		} = useSelectedServer();

		const { data: dnsZonesData } = useServerWhmDnsListzones(
			{
				uniq_id: server,
			},
			{ enabled: Boolean(server) },
		);

		const { data: sslDvProductData } = useSslDvFlatDetails();

		const sslOptionValues = sslDvProductData?.optionValues;

		const baseprice = Number(
			sslOptionValues?.['DomainSSL.1'].region_id_prices?.[1],
		);
		const wildcardPrice = Number(
			sslOptionValues?.['DomainSSL.1.Wildcard'].region_id_prices?.[1],
		);

		const wildcardPriceDiff = wildcardPrice - baseprice;

		const { data: certVerificationData, isLoading: isCertVerificationLoading } =
			useCertificateVerification(
				{ uniq_id: server, domain },
				{ enabled: Boolean(server && domain) },
			);

		const canAutoVerify =
			certVerificationData?.verification_methods.includes('auto');
		const canDnsVerify =
			certVerificationData?.verification_methods.includes('dns');
		const canMetatagVerify =
			certVerificationData?.verification_methods.includes('metatag');
		const canChooseDomain = hasCredentials && hasSniEnabled;

		return (
			<Stack ref={ref} sx={combineSx({ paddingBottom: 200 }, sx)} {...rest}>
				<Stack sx={{ gap: 500 }}>
					<Note
						icon={
							<InfoIcon
								sx={({ vars }) => ({
									color: vars.palette.uc.highlight.icon.main,
								})}
							/>
						}
					>
						Make sure you&apos;ve set up your domain inside cPanel before
						ordering a certificate
					</Note>
					<Stack>
						<HookedTextField
							name="server"
							select
							label="Server"
							size="large"
							slotProps={{ select: { displayEmpty: true } }}
							disabled={isHostServersLoading}
						>
							<MenuItem value="" sx={{ display: 'none' }}>
								Choose a server
							</MenuItem>
							{Object.entries(hostServers || {}).map(
								([hostname, { uniq_id: uniqId }]) => (
									<MenuItem key={uniqId} value={uniqId}>
										{hostname}
									</MenuItem>
								),
							)}
						</HookedTextField>
						<Collapse in={Boolean(server)}>
							<ServerCredentialsNote />
						</Collapse>
					</Stack>

					<HookedTextField
						name="domain"
						select
						label="Domain"
						size="large"
						slotProps={{ select: { displayEmpty: true } }}
						disabled={!canChooseDomain || !dnsZonesData?.zones.length}
					>
						<MenuItem value="" sx={{ display: 'none' }}>
							{dnsZonesData?.zones.length
								? 'Choose a domain'
								: 'No domains available'}
						</MenuItem>
						{dnsZonesData?.zones.map((zone) => (
							<MenuItem key={zone} value={zone}>
								{zone}
							</MenuItem>
						))}
					</HookedTextField>
				</Stack>
				<Collapse in={Boolean(server && domain)}>
					<Stack sx={{ gap: 500, mt: 500 }}>
						<Stack sx={{ gap: 300 }}>
							<TypeCard autorenew={autorenew} wildcard={wildcard} />

							<Stack sx={{ flexDirection: { md: 'row' }, gap: 200 }}>
								<VerificationMethod
									sx={{ flex: 1 }}
									loading={isCertVerificationLoading}
									auto={canAutoVerify}
									dns={canDnsVerify}
									metatag={canMetatagVerify}
								/>
								<Autorenew sx={{ flex: 1 }} />

								<Stack sx={{ flex: 1, gap: 100 }}>
									<Typography variant="body-sm" sx={{ fontWeight: 500 }}>
										CSR Options
									</Typography>
									<Stack
										sx={{
											flexDirection: 'row',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: 100,
										}}
									>
										<HookedCheckbox name="wildcard" label="Wildcard" />
										{wildcardPriceDiff > 0 && (
											<Typography variant="label-lg">
												+${wildcardPriceDiff}
											</Typography>
										)}
									</Stack>
								</Stack>
							</Stack>
						</Stack>

						<Stack sx={{ gap: 200 }}>
							<Typography variant="title-xs" sx={{ fontWeight: 500 }}>
								Contact info
							</Typography>
							<HookedTextField
								type="email"
								name="emailAddress"
								label="Email Address"
								placeholder="Enter your email"
							/>
							<Stack
								sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 200 }}
							>
								<HookedTextField
									name="organization"
									label="Organization"
									placeholder="Name of organization"
									fullWidth
								/>
								<HookedTextField
									name="organizationalUnit"
									label="Department"
									placeholder="Name of department"
									fullWidth
								/>
							</Stack>
							<Stack
								sx={{ flexDirection: { xs: 'column', md: 'row' }, gap: 200 }}
							>
								<HookedCountrySelect name="country" label="Country" fullWidth />
								<HookedRegionSelect
									name="state"
									country={country}
									fullWidth
									preferLongName
								/>
							</Stack>
							<HookedTextField
								name="city"
								label="City"
								placeholder="Your city or town"
							/>
						</Stack>
					</Stack>
				</Collapse>
			</Stack>
		);
	},
);

AutomatedMethod.displayName = 'AutomatedMethod';

export default AutomatedMethod;
